// Local persistence and the roster CRDT (last-writer-wins per member, keyed by member id).

// `?as=<name>` gives a tab its own identity so two tabs on one machine can test a room.
const ns = new URLSearchParams(globalThis.location?.search ?? '').get('as')
const key = (k) => (ns ? `gc.${ns}.` : 'gc.') + k

export const load = (k, fallback) => {
  try {
    const v = localStorage.getItem(key(k))
    return v ? JSON.parse(v) : fallback
  } catch {
    return fallback
  }
}

export const save = (k, v) => {
  try {
    localStorage.setItem(key(k), JSON.stringify(v))
  } catch {}
}

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'
export const randomId = (n) => Array.from(crypto.getRandomValues(new Uint8Array(n)), (b) => ALPHABET[b & 63]).join('')

// --- me -------------------------------------------------------------------

export const getMe = () => load('me', null)

export function saveMe({ name, phone, ig }) {
  const me = { id: getMe()?.id ?? randomId(12), name, phone, ...(ig ? { ig } : {}) }
  save('me', me)
  return me
}

// --- rooms ----------------------------------------------------------------

export function loadRoom(id, secret) {
  return load(`room.${id}`, null) ?? { id, secret, created: Date.now(), members: {} }
}

export function saveRoom(room) {
  save(`room.${room.id}`, room)
  const recent = load('rooms', []).filter((r) => r.id !== room.id)
  const names = activeMembers(room).map((m) => m.name.split(' ')[0])
  recent.unshift({ id: room.id, secret: room.secret, names, t: Date.now() })
  save('rooms', recent.slice(0, 12))
}

export const recentRooms = () => load('rooms', [])

// Write my current profile into the room, bumping its version only if something changed.
export function upsertMe(room, me) {
  const cur = room.members[me.id]
  const next = { id: me.id, name: me.name, phone: me.phone, ...(me.ig ? { ig: me.ig } : {}), j: cur?.j ?? Date.now(), ...(cur?.face ? { face: cur.face } : {}) }
  if (cur && !cur.deleted && cur.name === next.name && cur.phone === next.phone && cur.ig === next.ig) return false
  room.members[me.id] = { ...next, u: Math.max(Date.now(), (cur?.u ?? 0) + 1) }
  return true
}

// --- the group photo ------------------------------------------------------------------------
// room.photo: {t, w, h, faces, at, by}. `t` is the token for the encrypted copy on the server
// (vault.js), `faces` are [x, y, w, h] fractions of the photo. Changing the photo replaces it (newest wins)
// and clears every tag, since tags point at faces in one particular photo.

export function setPhoto(room, { t, w, h, faces }, by) {
  room.photo = { t, w, h, faces, by, at: Math.max(Date.now(), (room.photo?.at ?? 0) + 1) }
}

// Each person tags themselves: member.face = [photo.at, face index]. Tapping your own face again
// untags you.
export function tagMe(room, meId, index) {
  const cur = room.members[meId]
  if (!cur || cur.deleted || !room.photo) return false
  const face = index == null || faceIndex(room, cur) === index ? undefined : [room.photo.at, index]
  const { face: _, ...rest } = cur
  room.members[meId] = { ...rest, ...(face ? { face } : {}), u: Math.max(Date.now(), cur.u + 1) }
  return true
}

export const faceIndex = (room, m) =>
  room.photo && m?.face && m.face[0] === room.photo.at && m.face[1] < room.photo.faces.length ? m.face[1] : null

// Face index -> the person in it. If two people tapped the same face, the later tap wins.
export function faceOwners(room) {
  const owners = new Map()
  for (const m of activeMembers(room)) {
    const i = faceIndex(room, m)
    if (i != null && (!owners.has(i) || owners.get(i).u < m.u)) owners.set(i, m)
  }
  return owners
}

// The person's face index, unless someone later tapped the same face.
export function faceOf(room, m, owners = faceOwners(room)) {
  const i = faceIndex(room, m)
  return i != null && owners.get(i)?.id === m.id ? i : null
}

// --- the "save everyone" link ---------------------------------------------------------------
// One per group: room.link = {t, at}. If two phones make one at the same moment, the older wins
// everywhere, so the link in the text is the one that keeps being updated.

export function ensureLink(room, token) {
  if (room.link) return false
  room.link = { t: token, at: Date.now() }
  return true
}

// Add someone who isn't here (picked from contacts or typed in). If they scan in later, their
// own entry has the same phone and a newer version, so it replaces this one.
export function addOther(room, { name, phone, ig }, by) {
  if (activeMembers(room).some((m) => m.phone === phone)) return null
  const now = Date.now()
  const m = { id: randomId(12), name, phone, ...(ig ? { ig } : {}), j: now, u: now, by }
  room.members[m.id] = m
  return m
}

// Who started the group text and which numbers it went to, so nobody starts a duplicate thread
// and anyone who joins afterwards can be offered a fresh one.
export function markSent(room, by) {
  room.sent = { by, at: Math.max(Date.now(), (room.sent?.at ?? 0) + 1), phones: activeMembers(room).map((m) => m.phone) }
}

export function removeMember(room, id) {
  const cur = room.members[id]
  if (!cur) return
  room.members[id] = { id, name: cur.name, phone: cur.phone, j: cur.j, u: Math.max(Date.now(), cur.u + 1), deleted: true }
}

// Live people, oldest first, one entry per phone number (a person who re-joined from another
// browser shows up once, with their latest details).
export function activeMembers(room) {
  const byPhone = new Map()
  for (const m of Object.values(room.members)) {
    if (m.deleted) continue
    const prev = byPhone.get(m.phone)
    if (!prev || m.u > prev.u) byPhone.set(m.phone, m)
  }
  return [...byPhone.values()].sort((a, b) => a.j - b.j || (a.id < b.id ? -1 : 1))
}

// --- coming from split ------------------------------------------------------
// split (split.moreutils.com) links here with the room derived from the bill and the table's names
// (`t` after the #; see apps/split/src/lib/groupchat.ts). room.table keeps them on this phone, so
// the room can say who from the bill hasn't joined. Names match loosely ("Kevin" at the bill is
// "Kevin Host" here), and each person here counts for one name.

export const cleanTable = (names) =>
  names.map((n) => String(n).replace(/\s+/g, ' ').trim().slice(0, 40)).filter(Boolean).slice(0, 40)

const fold = (s) => s.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^\p{L}\p{N} ]/gu, '').trim()

export function stillToJoin(table, members) {
  const here = members.map((m) => fold(m.name))
  const used = new Set()
  const take = (match) => {
    const i = here.findIndex((h, k) => !used.has(k) && match(h))
    if (i >= 0) used.add(i)
    return i >= 0
  }
  // Exact names first, so "Sam Lee" isn't used up by "Sam" before the real Sam is matched.
  const exact = table.map((t) => take((h) => h === fold(t)))
  return table.filter((t, i) => !exact[i] && !take((h) => h.split(' ')[0] === fold(t).split(' ')[0]))
}

// --- sync -----------------------------------------------------------------

export const snapshot = (room, from) => ({
  from, created: room.created, sent: room.sent, photo: room.photo, link: room.link, members: Object.values(room.members),
})

const str = (v, max) => typeof v === 'string' && v.length <= max
const isPhone = (p) => typeof p === 'string' && /^\+\d{6,16}$/.test(p)
export const isHandle = (h) => typeof h === 'string' && /^[A-Za-z0-9._]{1,30}$/.test(h)
const isToken = (t) => typeof t === 'string' && /^[0-9A-Za-z]{16}$/.test(t)
const unit = (v) => Number.isFinite(v) && v >= -0.5 && v <= 1.5
const validPhoto = (p) =>
  p && isToken(p.t) && Number.isInteger(p.w) && Number.isInteger(p.h) && p.w > 0 && p.h > 0 && p.w <= 8192 && p.h <= 8192 &&
  Number.isFinite(p.at) && str(p.by, 32) && Array.isArray(p.faces) && p.faces.length <= 64 &&
  p.faces.every((f) => Array.isArray(f) && f.length === 4 && f.every(unit))
const validLink = (l) => l && isToken(l.t) && Number.isFinite(l.at)
const validFace = (f) => Array.isArray(f) && f.length === 2 && Number.isFinite(f[0]) && Number.isInteger(f[1]) && f[1] >= 0 && f[1] < 64
const validSent = (x) =>
  x && str(x.by, 32) && Number.isFinite(x.at) && Array.isArray(x.phones) && x.phones.length <= 128 && x.phones.every(isPhone)
const validMember = (m) =>
  m &&
  str(m.id, 32) &&
  str(m.name, 80) &&
  isPhone(m.phone) &&
  Number.isFinite(m.u) &&
  Number.isFinite(m.j) &&
  (m.by === undefined || str(m.by, 32)) &&
  (m.ig === undefined || isHandle(m.ig)) &&
  (m.face === undefined || validFace(m.face)) &&
  (m.deleted === undefined || m.deleted === true)

// Merge a peer's snapshot into ours. Returns the members we hadn't seen before.
export function merge(room, snap) {
  let changed = false
  const added = []
  if (Number.isFinite(snap?.created) && snap.created < room.created) room.created = snap.created
  if (validSent(snap?.sent) && snap.sent.at > (room.sent?.at ?? 0)) {
    const { by, at, phones } = snap.sent
    room.sent = { by, at, phones }
    changed = true
  }
  if (validPhoto(snap?.photo) && snap.photo.at > (room.photo?.at ?? 0)) {
    const { t, w, h, faces, at, by } = snap.photo
    room.photo = { t, w, h, faces, at, by }
    changed = true
  }
  const older = (a, b) => a.at < b.at || (a.at === b.at && a.t < b.t)
  if (validLink(snap?.link) && (!room.link || older(snap.link, room.link))) {
    room.link = { t: snap.link.t, at: snap.link.at }
    changed = true
  }
  for (const m of Array.isArray(snap?.members) ? snap.members : []) {
    if (!validMember(m)) continue
    const cur = room.members[m.id]
    if (cur && cur.u >= m.u) continue
    const { id, name, phone, ig, j, u, by, face, deleted } = m
    room.members[id] = deleted
      ? { id, name, phone, j, u, deleted }
      : { id, name, phone, ...(ig ? { ig } : {}), j, u, ...(by ? { by } : {}), ...(face ? { face } : {}) }
    if (!cur && !deleted) added.push(m)
    changed = true
  }
  return { changed, added }
}
